import type { ImageOrientation } from './types'

/**
 * EXIF orientation, ported from the duplicated WPF helpers in
 * MonitoringWindow.xaml.cs and PrintWithLayout.xaml.cs, which both read
 * `/app1/ifd/{ushort=274}`.
 *
 * Only tag 274 is needed, so this is a direct APP1/TIFF walk rather than a
 * dependency. Mirrored orientations (2/4/5/7) are ignored, matching the
 * original's rotate table.
 *
 * The fallback matters: EVERY failure path in the WPF code returns "Landscape",
 * and the print/composite decisions hang off that, so it is reproduced exactly.
 */

export const ORIENTATION_FALLBACK: ImageOrientation = 'Landscape'

/** Degrees clockwise to rotate for a given EXIF tag value. */
export function rotationForExif(exif: number): 0 | 90 | 180 | 270 {
  switch (exif) {
    case 3:
      return 180
    case 6:
      return 90
    case 8:
      return 270
    default:
      return 0
  }
}

/**
 * Reads EXIF tag 274. Returns 1 when the tag is absent or the bytes are not a
 * JPEG we can walk — 1 means "no rotation", which is the original's behaviour.
 */
export function readExifOrientation(bytes: ArrayBuffer | Uint8Array): number {
  const buf = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength)

  // JPEG SOI
  if (buf.length < 4 || view.getUint16(0) !== 0xffd8) return 1

  let offset = 2
  while (offset + 4 <= buf.length) {
    const marker = view.getUint16(offset)
    // Every JPEG marker starts 0xFF; anything else means we've lost the stream.
    if ((marker & 0xff00) !== 0xff00) return 1

    const segmentLength = view.getUint16(offset + 2)
    if (segmentLength < 2) return 1

    // APP1 with an "Exif\0\0" header
    if (marker === 0xffe1 && offset + 10 <= buf.length) {
      if (
        view.getUint32(offset + 4) === 0x45786966 && // "Exif"
        view.getUint16(offset + 8) === 0x0000
      ) {
        return readTag274(view, offset + 10, Math.min(offset + 2 + segmentLength, buf.length))
      }
    }

    // Start of scan — no more metadata past here.
    if (marker === 0xffda) return 1
    offset += 2 + segmentLength
  }
  return 1
}

/** Walks the TIFF header at `tiffStart` looking for IFD0 tag 274. */
function readTag274(view: DataView, tiffStart: number, end: number): number {
  if (tiffStart + 8 > end) return 1

  const byteOrder = view.getUint16(tiffStart)
  let little: boolean
  if (byteOrder === 0x4949) little = true
  else if (byteOrder === 0x4d4d) little = false
  else return 1

  if (view.getUint16(tiffStart + 2, little) !== 0x002a) return 1

  const ifd0 = tiffStart + view.getUint32(tiffStart + 4, little)
  if (ifd0 + 2 > end) return 1

  const entryCount = view.getUint16(ifd0, little)
  for (let i = 0; i < entryCount; i++) {
    const entry = ifd0 + 2 + i * 12
    if (entry + 12 > end) return 1
    if (view.getUint16(entry, little) === 274) {
      // SHORT value sits in the first 2 bytes of the 4-byte value field.
      return view.getUint16(entry + 8, little)
    }
  }
  return 1
}

/**
 * Portrait / Landscape / Square for a photo, matching
 * `GetImageOrientationFromExif`: read the stored pixel dimensions, swap them
 * when the EXIF tag is 6 or 8, then compare.
 */
export function orientationFrom(
  pixelWidth: number,
  pixelHeight: number,
  exif: number,
): ImageOrientation {
  let w = pixelWidth
  let h = pixelHeight
  if (exif === 6 || exif === 8) {
    w = pixelHeight
    h = pixelWidth
  }
  if (w > h) return 'Landscape'
  if (w < h) return 'Portrait'
  return 'Square'
}

/** Decoded photo plus everything the compositor needs to place it. */
export interface DecodedImage {
  bitmap: ImageBitmap
  /** Dimensions AFTER rotation has been applied to the bitmap. */
  width: number
  height: number
  orientation: ImageOrientation
  exif: number
}

/**
 * Decodes a photo with its EXIF rotation already baked in, and reports the
 * orientation the slot gate will compare against.
 *
 * `imageOrientation: 'from-image'` makes the browser apply the same rotations
 * that `RotateImageByExifOrientation` applied with RotateFlip, so the decoded
 * bitmap's own dimensions are already the swapped ones.
 */
export async function decodeImage(blob: Blob): Promise<DecodedImage> {
  let exif = 1
  try {
    exif = readExifOrientation(await blob.arrayBuffer())
  } catch {
    exif = 1
  }

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' })
  } catch {
    // Malformed or unsupported file: let the caller deal with it, but keep the
    // failure shape the same as the original (which fell back to Landscape).
    throw new Error('decodeImage: could not decode image')
  }

  // The bitmap is already rotated, so its own dimensions give the orientation
  // directly — no second swap.
  const orientation = orientationFrom(bitmap.width, bitmap.height, 1)
  return { bitmap, width: bitmap.width, height: bitmap.height, orientation, exif }
}
